/**
 * Phase O — Per-section Mobile SFG Put-Away.
 *
 * Mobile-optimised flow for `/sections/dispatch/:section/mobile/sfg-put-away`.
 * Backend uses the simpler per-section SFG put-away API
 * (`/api/per-section/{slug}/sfg-put-away/...`) — same surface as the
 * desktop clone (`PerSectionSfgPutAwayClone.jsx`, Phase D5). Salem's
 * `MobileSFGPutAway.jsx` is paired with this file in
 * `SALEM_CLONE_DRIFT.md` row 8 (Phase F discipline).
 *
 * Visual language matches the Salem mobile (dark theme, large touch
 * targets, vibration feedback, progress dots) so muscle memory carries
 * across sites.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, User, Package, MapPin, CheckCircle, RotateCcw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo',
  art_silk: 'Art Silk',
  kids: 'Kids',
};

const PerSectionMobileSfgPutAway = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug];
  const mobileHome = `/sections/dispatch/${slug}/mobile`;
  const apiBase = `/per-section/${slug}/sfg-put-away`;

  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);
  const binInputRef = useRef(null);

  const [step, setStep] = useState('staff'); // staff, scan, confirm, success
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [bundleCode, setBundleCode] = useState('');
  const [bundleDetails, setBundleDetails] = useState(null);
  const [suggestedBin, setSuggestedBin] = useState(null);
  const [binCode, setBinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [putAwayCount, setPutAwayCount] = useState(0);

  useEffect(() => {
    if (step === 'staff') setTimeout(() => staffInputRef.current?.focus(), 100);
    else if (step === 'scan') setTimeout(() => bundleInputRef.current?.focus(), 100);
    else if (step === 'confirm') setTimeout(() => binInputRef.current?.focus(), 100);
  }, [step]);

  const vibrate = (pattern = [50]) => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(pattern);
  };

  const handleStaffValidate = async () => {
    if (!staffId.trim()) {
      toast.error('Please scan Staff ID');
      vibrate([100, 50, 100]);
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.get(`/validate/staff/${staffId}`);
      if (response.data.valid) {
        setStaffName(response.data.staff_name);
        setStep('scan');
        vibrate([50]);
        toast.success(`Welcome, ${response.data.staff_name}!`);
      } else {
        toast.error('Invalid Staff ID');
        vibrate([100, 50, 100]);
      }
    } catch (error) {
      toast.error('Staff validation failed');
      vibrate([100, 50, 100]);
    } finally {
      setLoading(false);
    }
  };

  const handleBundleScan = async () => {
    if (!bundleCode.trim()) {
      toast.error('Please scan bundle barcode');
      vibrate([100, 50, 100]);
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.post(
        `${apiBase}/validate-bundle/${bundleCode}?staff_id=${encodeURIComponent(staffId)}`
      );
      setBundleDetails(response.data.bundle);
      setSuggestedBin(response.data.suggested_bin || null);
      setBinCode('');
      setStep('confirm');
      vibrate([50]);
      toast.success('Bundle validated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bundle not found');
      vibrate([100, 50, 100]);
      setBundleCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPutAway = async () => {
    if (!binCode.trim()) {
      toast.error('Please enter bin location');
      vibrate([100, 50, 100]);
      return;
    }
    try {
      setLoading(true);
      await apiClient.post(apiBase, {
        staff_id: staffId,
        bundle_code: bundleCode,
        bin_location: binCode,
      });
      setPutAwayCount((p) => p + 1);
      setStep('success');
      vibrate([50, 100, 50]);
      toast.success('Put-away complete!');
      setTimeout(resetForNextBundle, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Put-away failed');
      vibrate([100, 50, 100]);
    } finally {
      setLoading(false);
    }
  };

  const resetForNextBundle = () => {
    setBundleCode('');
    setBundleDetails(null);
    setSuggestedBin(null);
    setBinCode('');
    setStep('scan');
  };

  const resetAll = () => {
    setStaffId('');
    setStaffName('');
    setBundleCode('');
    setBundleDetails(null);
    setSuggestedBin(null);
    setBinCode('');
    setPutAwayCount(0);
    setStep('staff');
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') action();
  };

  if (!sectionName) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-6">
        <AlertTriangle className="w-10 h-10 text-amber-400 mb-3" />
        <h2 className="text-lg font-bold">Unknown section</h2>
        <p className="text-sm text-gray-400 mt-1">No catalog entry for slug <code className="font-mono">{slug}</code>.</p>
      </div>
    );
  }

  const stepOrder = ['staff', 'scan', 'confirm', 'success'];
  const currentStepIdx = stepOrder.indexOf(step);

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col" data-testid={`per-section-mobile-sfg-put-away-${slug}`}>
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-purple-800 px-4 py-4 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(mobileHome)}
            className="text-white hover:bg-white/20 -ml-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="text-center">
            <h1 className="text-xl font-bold">SFG Put-Away</h1>
            <p className="text-xs text-purple-200">{sectionName} Section{staffName ? ` · ${staffName}` : ''}</p>
          </div>
          <div className="w-10" />
        </div>
        <div className="mt-4 flex items-center gap-2">
          {stepOrder.map((s, i) => (
            <div key={s} className={`flex-1 h-2 rounded-full ${i <= currentStepIdx ? 'bg-white' : 'bg-white/30'}`} />
          ))}
        </div>
      </header>

      {/* Session Stats */}
      {step !== 'staff' && (
        <div className="bg-gray-800 px-4 py-2 flex items-center justify-between text-sm">
          <span className="text-gray-400">Session Put-Aways:</span>
          <span className="font-bold text-green-400" data-testid="session-counter">{putAwayCount}</span>
        </div>
      )}

      <main className="flex-1 p-4 flex flex-col">
        {/* Staff */}
        {step === 'staff' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-6 space-y-6">
              <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto">
                <User className="w-10 h-10 text-purple-400" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Scan Staff ID</h2>
                <p className="text-gray-400">Scan your badge to begin</p>
              </div>
              <Input
                ref={staffInputRef}
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                placeholder="Staff ID"
                className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl"
                data-testid="mobile-staff-input"
              />
              <Button
                onClick={handleStaffValidate}
                disabled={loading || !staffId}
                className="w-full h-16 text-xl font-bold bg-purple-600 hover:bg-purple-700 rounded-xl"
                data-testid="mobile-staff-validate-btn"
              >
                {loading ? 'Validating...' : 'Continue'}
              </Button>
            </div>
          </div>
        )}

        {/* Bundle scan */}
        {step === 'scan' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-6 space-y-6">
              <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                <Package className="w-10 h-10 text-blue-400" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Scan Bundle</h2>
                <p className="text-gray-400">Scan the bundle barcode</p>
              </div>
              <Input
                ref={bundleInputRef}
                value={bundleCode}
                onChange={(e) => setBundleCode(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleBundleScan)}
                placeholder="Bundle Barcode"
                className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                data-testid="mobile-bundle-input"
              />
              <Button
                onClick={handleBundleScan}
                disabled={loading || !bundleCode}
                className="w-full h-16 text-xl font-bold bg-blue-600 hover:bg-blue-700 rounded-xl"
                data-testid="mobile-bundle-validate-btn"
              >
                {loading ? 'Validating...' : 'Validate Bundle'}
              </Button>
            </div>
          </div>
        )}

        {/* Confirm bin */}
        {step === 'confirm' && bundleDetails && (
          <div className="flex-1 flex flex-col space-y-4">
            <div className="bg-gray-800 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-400">Bundle</span>
                <span className="font-mono font-bold text-lg" data-testid="bundle-code-display">{bundleCode}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-700 rounded-xl p-3">
                  <p className="text-gray-400 text-xs">SKU</p>
                  <p className="font-medium truncate">{bundleDetails.sku_code || '-'}</p>
                </div>
                <div className="bg-gray-700 rounded-xl p-3">
                  <p className="text-gray-400 text-xs">Qty</p>
                  <p className="font-bold text-xl">{bundleDetails.usn_count ?? bundleDetails.total_pieces ?? '-'}</p>
                </div>
                <div className="bg-gray-700 rounded-xl p-3 col-span-2">
                  <p className="text-gray-400 text-xs">Design</p>
                  <p className="font-medium truncate">{bundleDetails.design_name || '-'}</p>
                </div>
              </div>
            </div>

            {suggestedBin && (
              <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-2xl p-4" data-testid="suggested-bin-card">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-green-200">Suggested Location</p>
                    <p className="text-2xl font-bold">{suggestedBin.bin_code}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gray-800 rounded-2xl p-4 space-y-4">
              <div className="text-center">
                <h3 className="font-bold mb-1">Confirm Bin Location</h3>
                <p className="text-sm text-gray-400">Scan bin or enter manually</p>
              </div>
              <Input
                ref={binInputRef}
                value={binCode}
                onChange={(e) => setBinCode(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleConfirmPutAway)}
                placeholder="Bin Location"
                className="h-14 text-lg text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                data-testid="mobile-bin-input"
              />
            </div>

            <div className="mt-auto space-y-3">
              <Button
                onClick={handleConfirmPutAway}
                disabled={loading || !binCode}
                className="w-full h-16 text-xl font-bold bg-green-600 hover:bg-green-700 rounded-xl"
                data-testid="mobile-confirm-putaway-btn"
              >
                {loading ? 'Processing...' : 'Confirm Put-Away'}
              </Button>
              <Button
                variant="outline"
                onClick={resetForNextBundle}
                className="w-full h-12 border-gray-600 text-gray-300 hover:bg-gray-800 rounded-xl"
                data-testid="mobile-cancel-rescan-btn"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Cancel & Rescan
              </Button>
            </div>
          </div>
        )}

        {/* Success */}
        {step === 'success' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-8 text-center space-y-6" data-testid="mobile-success-card">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto animate-bounce">
                <CheckCircle className="w-14 h-14 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-green-400">Success!</h2>
                <p className="text-gray-400 mt-2">Bundle stored at {binCode}</p>
              </div>
              <p className="text-sm text-gray-500">Ready for next bundle...</p>
            </div>
          </div>
        )}
      </main>

      {step !== 'staff' && step !== 'success' && (
        <footer className="bg-gray-800 px-4 py-3 safe-area-inset-bottom">
          <Button
            variant="ghost"
            onClick={resetAll}
            className="w-full text-gray-400 hover:text-white"
            data-testid="mobile-end-session-btn"
          >
            End Session
          </Button>
        </footer>
      )}
    </div>
  );
};

export default PerSectionMobileSfgPutAway;
